import React, { useEffect, useState, useCallback } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { Award, Pencil, Check, X, CalendarClock, Users, Plus, Search, Crown, Layers } from "lucide-react";
import { toast } from "sonner";

export default function Groups() {
  const { eventId } = useAdminEvent();
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ arrival: "", departure: "" });
  const [builder, setBuilder] = useState(null);

  const loadAll = useCallback(() => {
    api.adminGroups(eventId).then(setGroups).catch(() => {});
    api.mgrClassSummary(eventId).then(setSummary).catch(() => {});
    api.mgrAudiences(eventId).then((r) => setAudiences(r.audiences || [])).catch(() => {});
  }, [eventId]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const save = async (g) => {
    try {
      await api.updateGroupDates(eventId, g.id, { default_arrival_date: form.arrival || null, default_departure_date: form.departure || null });
      toast.success(`Updated ${g.name} default stay`); setEditing(null); loadAll();
    } catch (e) { toast.error("Could not update group dates"); }
  };

  const Dim = ({ title, icon: Icon, data }) => (
    <div className="premium-card p-4" data-testid={`class-dim-${title.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-3 text-[hsl(var(--primary))] font-semibold"><Icon className="h-4 w-4 text-[hsl(var(--secondary))]" /> {title}</div>
      <div className="space-y-1.5">
        {Object.entries(data || {}).map(([label, count]) => (
          <div key={label} className="flex items-center justify-between text-sm">
            <span className="text-[hsl(var(--primary)/0.85)]">{label}</span>
            <span className="font-serif text-lg text-[hsl(var(--primary))]" data-testid={`class-count-${label}`}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageIntro>Attendees are described by three independent dimensions — Attendee Type, Recognition and Leadership — plus reusable Audience Groups for sessions and communications. Counts are calculated live from canonical membership.</PageIntro>

      <div>
        <h2 className="font-serif text-xl text-[hsl(var(--primary))] mb-3">Classification {summary && <span className="text-sm text-[hsl(var(--muted-foreground))]">· {summary.total} attendees</span>}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Dim title="Attendee Type" icon={Users} data={summary?.attendee_type} />
          <Dim title="Recognition" icon={Award} data={summary?.recognition} />
          <Dim title="Leadership" icon={Crown} data={summary?.leadership} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl text-[hsl(var(--primary))]">Audience Groups</h2>
          <button data-testid="create-audience-btn" onClick={() => setBuilder({ mode: "create", name: "", base: { recognitions: [], leaderships: [], attendee_types: [] }, includes: [], excludes: [] })}
            className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90"><Plus className="h-4 w-4" /> Create Audience Group</button>
        </div>
        {audiences.length === 0 ? (
          <div className="premium-card p-6 text-center text-[hsl(var(--muted-foreground))] text-sm" data-testid="audiences-empty">No reusable audiences yet. Create one (e.g. PC Dinner Eligible).</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="audience-list">
            {audiences.map((a) => (
              <div key={a.id} data-testid="audience-card" className="premium-card p-4 flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-[hsl(var(--secondary)/0.12)] grid place-items-center text-[hsl(var(--secondary))] shrink-0"><Layers className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[hsl(var(--primary))]">{a.name}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                    {[...(a.base?.recognitions || []), ...(a.base?.leaderships || []), ...(a.base?.attendee_types || [])].join(", ") || "No base rules"}
                    {a.includes?.length ? ` · +${a.includes.length} incl` : ""}{a.excludes?.length ? ` · -${a.excludes.length} excl` : ""}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-sm"><span className="font-serif text-xl text-[hsl(var(--primary))]" data-testid="audience-member-count">{a.member_count}</span> <span className="text-[hsl(var(--muted-foreground))]">members</span></span>
                    <button data-testid="audience-edit" onClick={() => setBuilder({ mode: "edit", ...a })} className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                    <button data-testid="audience-archive" onClick={async () => { await api.mgrAudienceArchive(eventId, a.id, true); toast.success("Archived"); loadAll(); }} className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">Archive</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-serif text-xl text-[hsl(var(--primary))] mb-3">Recognition Tiers · default participation window</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {groups.filter((g) => g.is_recognition).map((g) => (
            <div key={g.id} data-testid="admin-group-card" className="premium-card p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-[hsl(var(--primary))]">{g.name}</span>
                <span className="text-sm"><span className="font-serif text-lg text-[hsl(var(--primary))]">{g.attendee_count}</span> <span className="text-[hsl(var(--muted-foreground))]">attendees</span></span>
              </div>
              <div className="mt-2 rounded-xl bg-[hsl(var(--muted)/0.6)] p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Default window</div>
                  {editing !== g.id && <button data-testid="admin-group-edit-dates" onClick={() => { setEditing(g.id); setForm({ arrival: g.default_arrival_date || "", departure: g.default_departure_date || "" }); }} className="text-[hsl(var(--secondary))] hover:opacity-80"><Pencil className="h-3.5 w-3.5" /></button>}
                </div>
                {editing === g.id ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input data-testid="admin-group-arrival-input" type="date" value={form.arrival} onChange={(e) => setForm({ ...form, arrival: e.target.value })} className="flex-1 rounded-lg border border-[hsl(var(--border))] px-2 py-1.5 text-sm" />
                      <span className="text-[hsl(var(--muted-foreground))]">-&gt;</span>
                      <input type="date" value={form.departure} onChange={(e) => setForm({ ...form, departure: e.target.value })} className="flex-1 rounded-lg border border-[hsl(var(--border))] px-2 py-1.5 text-sm" />
                    </div>
                    <div className="flex gap-2">
                      <button data-testid="admin-group-save-dates" onClick={() => save(g)} className="inline-flex items-center gap-1 rounded-lg bg-[hsl(var(--primary))] text-white px-3 py-1.5 text-xs font-semibold"><Check className="h-3.5 w-3.5" /> Save</button>
                      <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-semibold"><X className="h-3.5 w-3.5" /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm font-medium text-[hsl(var(--primary))]">{g.default_arrival_date ? `${g.default_arrival_date} -> ${g.default_departure_date}` : <span className="text-[hsl(var(--muted-foreground))] italic">Not set</span>}</div>
                )}
              </div>
            </div>
          ))}
          {["Upcoming Talent Winner", "Milestone Achiever"].map((name) => {
            const cnt = summary?.recognition?.[name] ?? 0;
            return (
              <div key={name} data-testid="admin-group-card" className="premium-card p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-[hsl(var(--primary))]">{name}</span>
                  <span className="text-sm"><span className="font-serif text-lg text-[hsl(var(--primary))]">{cnt}</span> <span className="text-[hsl(var(--muted-foreground))]">attendees</span></span>
                </div>
                <div className="mt-2 rounded-xl bg-[hsl(var(--muted)/0.6)] p-3">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))] flex items-center gap-1.5 mb-1"><CalendarClock className="h-3.5 w-3.5" /> Default window</div>
                  <div className="text-sm font-medium text-[hsl(var(--muted-foreground))] italic">Inherits Non-PC default participation window</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {builder && <AudienceBuilder eventId={eventId} builder={builder} onClose={() => setBuilder(null)} onSaved={() => { setBuilder(null); loadAll(); }} />}
    </div>
  );
}

function AudienceBuilder({ eventId, builder, onClose, onSaved }) {
  const [name, setName] = useState(builder.name || "");
  const [base, setBase] = useState({ recognitions: builder.base?.recognitions || [], leaderships: builder.base?.leaderships || [], attendee_types: builder.base?.attendee_types || [] });
  const [includes, setIncludes] = useState(builder.includes || []);
  const [excludes, setExcludes] = useState(builder.excludes || []);
  const [roster, setRoster] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.adminAttendees(eventId).then((r) => setRoster(r.attendees || r || [])).catch(() => setRoster([])); }, [eventId]);

  const RECS = ["MDC Winner", "PC Winner", "Sprint to PC Winner", "Upcoming Talent Winner", "Milestone Achiever"];
  const LEADS = ["Executive Leadership", "Leadership"];
  const TYPES = ["PC Attendee", "Non-PC Attendee"];
  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const nmeOf = (a) => `${a.preferred_name || a.first_name || ""} ${a.last_name || ""}`.trim() || a.email;

  const submit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const body = { name: name.trim(), base, includes, excludes };
      const saved = builder.mode === "create" ? await api.mgrAudienceCreate(eventId, body) : await api.mgrAudienceUpdate(eventId, builder.id, body);
      const m = await api.mgrAudienceMembers(eventId, saved.id);
      toast.success(`Saved "${saved.name}" - ${m.count} members`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const filtered = roster.filter((a) => { const t = (nmeOf(a) + " " + (a.email || "")).toLowerCase(); return q && t.includes(q.toLowerCase()); }).slice(0, 8);
  const Chip = ({ list, setList, v }) => (
    <button type="button" onClick={() => toggle(list, setList, v)} data-testid={`base-chip-${v}`}
      className={`text-xs px-2.5 py-1 rounded-full border ${list.includes(v) ? "bg-[hsl(var(--secondary)/0.15)] border-[hsl(var(--secondary))] text-[hsl(var(--secondary))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>{v}</button>
  );

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center bg-black/50 p-4" data-testid="audience-builder">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-serif text-lg text-[hsl(var(--primary))]">{builder.mode === "create" ? "Create Audience Group" : "Edit Audience"}</h3><button data-testid="audience-builder-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button></div>
        <label className="text-xs font-semibold text-[hsl(var(--primary))]">Name</label>
        <input data-testid="audience-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. PC Dinner Eligible" className="mt-1 mb-3 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" />
        <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-1">Base rules - Recognition</div>
        <div className="flex flex-wrap gap-1.5 mb-2">{RECS.map((v) => <Chip key={v} list={base.recognitions} setList={(x) => setBase({ ...base, recognitions: x })} v={v} />)}</div>
        <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-1">Leadership</div>
        <div className="flex flex-wrap gap-1.5 mb-2">{LEADS.map((v) => <Chip key={v} list={base.leaderships} setList={(x) => setBase({ ...base, leaderships: x })} v={v} />)}</div>
        <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-1">Attendee Type</div>
        <div className="flex flex-wrap gap-1.5 mb-3">{TYPES.map((v) => <Chip key={v} list={base.attendee_types} setList={(x) => setBase({ ...base, attendee_types: x })} v={v} />)}</div>

        <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-1">Manual include / exclude</div>
        <div className="relative mb-2"><Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" /><input data-testid="audience-attendee-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search attendee to include/exclude" className="w-full pl-8 pr-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" /></div>
        {q && (
          <div className="border border-[hsl(var(--border))] rounded-lg mb-2 divide-y">
            {filtered.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-2 py-1.5 text-sm">
                <span className="truncate">{nmeOf(a)}</span>
                <span className="flex gap-1">
                  <button data-testid="audience-add-include" onClick={() => setIncludes([...new Set([...includes, a.id])])} className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(140_40%_92%)] text-[hsl(140_50%_30%)]">+incl</button>
                  <button data-testid="audience-add-exclude" onClick={() => setExcludes([...new Set([...excludes, a.id])])} className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(0_60%_94%)] text-[hsl(0_60%_40%)]">-excl</button>
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1 mb-3 text-[11px]">
          {includes.map((id) => <span key={id} className="px-2 py-0.5 rounded-full bg-[hsl(140_40%_92%)] text-[hsl(140_50%_30%)] flex items-center gap-1">+{(roster.find((a) => a.id === id) && nmeOf(roster.find((a) => a.id === id))) || id}<button onClick={() => setIncludes(includes.filter((x) => x !== id))}><X className="h-3 w-3" /></button></span>)}
          {excludes.map((id) => <span key={id} className="px-2 py-0.5 rounded-full bg-[hsl(0_60%_94%)] text-[hsl(0_60%_40%)] flex items-center gap-1">-{(roster.find((a) => a.id === id) && nmeOf(roster.find((a) => a.id === id))) || id}<button onClick={() => setExcludes(excludes.filter((x) => x !== id))}><X className="h-3 w-3" /></button></span>)}
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Cancel</button><button data-testid="audience-save" disabled={busy} onClick={submit} className="px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">Save Audience</button></div>
      </div>
    </div>
  );
}
